/**
 * Blog Related Articles
 *
 * Related article cards section at the end of blog post
 */
'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import { Post } from '@/types/post';

interface BlogRelatedArticlesProps {
  posts: Post[];
}

export default function BlogRelatedArticles({ posts }: BlogRelatedArticlesProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Use mock data if no posts provided
  const relatedPosts =
    posts.length > 0
      ? posts
      : [
          {
            slug: 'reduce-marketplace-fraud-model-ensembles',
            title: 'How to Reduce Marketplace Fraud by 40% with Model Ensembles',
            excerpt:
              'A practical guide to lowering fraud rates in marketplaces using advanced model ensembles and behavioral signals. Learn the....',
            image: '/images/blog/Andoria-AI-technology-blogHero.webp',
            category: 'AI & Automation',
            author: {
              name: 'Ose David',
              avatar: '/images/blog/ellipse-5.webp',
            },
            date: '2025-11-04',
            readTime: '8 min read',
            content: '',
          },
          {
            slug: 'reduce-marketplace-fraud-model-ensembles',
            title: 'How to Reduce Marketplace Fraud by 40% with Model Ensembles',
            excerpt:
              'A practical guide to lowering fraud rates in marketplaces using advanced model ensembles and behavioral signals. Learn the....',
            image: '/images/blog/Andoria-AI-technology-blogHero.webp',
            category: 'AI & Automation',
            author: {
              name: 'Ose David',
              avatar: '/images/blog/ellipse-5.webp',
            },
            date: '2025-11-04',
            readTime: '8 min read',
            content: '',
          },
          {
            slug: 'reduce-marketplace-fraud-model-ensembles',
            title: 'How to Reduce Marketplace Fraud by 40% with Model Ensembles',
            excerpt:
              'A practical guide to lowering fraud rates in marketplaces using advanced model ensembles and behavioral signals. Learn the....',
            image: '/images/blog/Andoria-AI-technology-blogHero.webp',
            category: 'AI & Automation',
            author: {
              name: 'Ose David',
              avatar: '/images/blog/ellipse-5.webp',
            },
            date: '2025-11-04',
            readTime: '8 min read',
            content: '',
          },
        ];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
      <div className="flex flex-col gap-8">
        <h2 className="text-3xl font-bold text-androia-text font-manrope">Related Articles</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {relatedPosts.slice(0, 3).map((post, index) => (
            <article key={`${post.slug}-${index}`} className="flex flex-col group cursor-pointer">
              {/* Image */}
              <div className="relative h-[340px] rounded-3xl overflow-hidden mb-6">
                <Image
                  src={post.image}
                  alt={post.title}
                  fill
                  className="object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/70" />

                {/* Category Badge */}
                <div className="absolute top-4 right-4 px-4 py-3 rounded-full bg-androia-blue-dark backdrop-blur-[15px] text-white text-xs font-semibold font-manrope">
                  {post.category}
                </div>
              </div>

              {/* Content */}
              <div className="flex flex-col gap-6 px-4">
                <div className="flex flex-col gap-4">
                  <h3 className="text-2xl font-bold text-androia-text font-manrope leading-tight group-hover:text-androia-blue transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-base text-androia-gray-500 font-manrope leading-relaxed">
                    {post.excerpt}
                  </p>
                </div>

                <div className="flex justify-between items-end">
                  <Link
                    href={`/blog/${post.slug}`}
                    className="inline-flex items-center gap-1 text-lg font-semibold text-androia-blue hover:text-androia-blue-mid transition-colors font-manrope group"
                  >
                    Read Full Article
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </Link>

                  <div className="flex items-center gap-2">
                    <div className="flex flex-col items-end gap-3">
                      <span className="text-lg font-semibold text-androia-text font-manrope">
                        {post.author.name}
                      </span>
                      <span className="text-sm text-androia-gray-900 font-manrope">
                        {formatDate(post.date)}
                      </span>
                    </div>
                    <div className="relative">
                      <Image
                        src={post.author.avatar}
                        alt={post.author.name}
                        width={36}
                        height={36}
                        className="rounded-full border border-white"
                      />
                      <div className="absolute -inset-0.5 rounded-full border border-androia-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
